/** Per-planet multipliers from Colonization and Prospectors (GDD §3.7–3.8). */

import { COLONY_HAUL_PER_LEVEL, COLONY_MAX_LEVEL, COLONY_MINE_PER_LEVEL, colonyCost } from '../content/colonies';
import {
  PROSPECTORS, PROSPECTOR_MAX_LEVEL, PROSPECTOR_ODDS, RECRUIT_COST_SC, prospectorBonus, prospectorDef,
  type ProspectorRole,
} from '../content/prospectors';
import type { Ingredient } from '../content/resources';
import { nextRandom, pickWeighted } from './rng';
import { cutInputs, roomCut, roomMult } from './rooms';
import { hasFeature, hasProject, projectEfficiency } from './projects';
import { ROVER_RESUPPLY } from '../content/rovers';
import type { GameState } from './state';

export interface PlanetMults {
  mine: number;
  ship: number;
  cargo: number;
}

/** Colony and Prospector multipliers on one planet. */
export function planetMults(state: GameState, index: number): PlanetMults {
  const p = state.run.planets[index]!;
  const relations = roomMult(state, 'colonyBonus');
  const out: PlanetMults = {
    mine: 1 + COLONY_MINE_PER_LEVEL * p.colony * relations,
    ship: 1 + COLONY_HAUL_PER_LEVEL * p.colony * relations,
    cargo: 1 + COLONY_HAUL_PER_LEVEL * p.colony * relations,
  };
  // Rover Resupply: the planet being scanned runs ×2.5 (IPM).
  if (!state.run.challenge && state.rover?.planet === index && hasProject(state, 'rover_resupply')) {
    out.mine *= ROVER_RESUPPLY;
    out.ship *= ROVER_RESUPPLY;
    out.cargo *= ROVER_RESUPPLY;
  }
  // Prospectors only work once the Management project is done this galaxy, as IPM's managers.
  const pid = state.assignments[String(index)];
  if (pid && state.prospectors[pid] && hasFeature(state, 'management')) {
    const def = prospectorDef(pid);
    out[def.role] *= 1 + prospectorBonus(def, state.prospectors[pid]!) * roomMult(state, 'prospector');
  }
  return out;
}

// ── Colonization ───────────────────────────────────────────────────────────

/** Items for the next colony level after the Terrarium. Empty at max. */
export function nextColonyCost(state: GameState, index: number): Ingredient[] {
  const cut = 1 - (1 - roomCut(state, 'colonyCost')) * (1 - projectEfficiency(state, 'colony'));
  return cutInputs(colonyCost(index, state.run.planets[index]!.colony), cut);
}

export function canColonize(state: GameState, index: number): boolean {
  const p = state.run.planets[index];
  if (!hasFeature(state, 'colonization') || !p?.owned || p.colony >= COLONY_MAX_LEVEL) return false;
  return nextColonyCost(state, index).every((i) => (state.run.hold[i.id] ?? 0) >= i.qty);
}

export function colonize(state: GameState, index: number): boolean {
  if (!canColonize(state, index)) return false;
  for (const i of nextColonyCost(state, index)) state.run.hold[i.id]! -= i.qty;
  state.run.planets[index]!.colony += 1;
  return true;
}

// ── Prospectors ────────────────────────────────────────────────────────────

/** Recruits one Prospector for SC; a duplicate levels the one you have. Returns its id. */
export function recruit(state: GameState): string | null {
  if (state.sc < RECRUIT_COST_SC) return null;
  const id = grantProspector(state);
  if (id) state.sc -= RECRUIT_COST_SC;
  return id;
}

/** A free Prospector (rover finds, recruits): rarity rolled, a duplicate levels up. */
export function grantProspector(state: GameState): string | null {
  // Templates already at max level drop out of the pool.
  const open = PROSPECTORS.filter((p) => (state.prospectors[p.id] ?? 0) < PROSPECTOR_MAX_LEVEL);
  if (open.length === 0) return null;
  const rarities = Object.fromEntries(Object.entries(PROSPECTOR_ODDS).filter(([r]) => open.some((p) => p.rarity === r)));
  const rarity = pickWeighted(state, rarities as Record<string, number>);
  const pool = open.filter((p) => p.rarity === rarity);
  const pick = pool[Math.floor(nextRandom(state) * pool.length)]!;
  state.prospectors[pick.id] = (state.prospectors[pick.id] ?? 0) + 1;
  return pick.id;
}

/** Assigns a Prospector to a planet, moving them off any other planet. Null clears. */
export function assignProspector(state: GameState, index: number, id: string | null): boolean {
  if (id !== null && !state.prospectors[id]) return false;
  for (const [planet, pid] of Object.entries(state.assignments)) if (pid === id) delete state.assignments[planet];
  if (id === null) delete state.assignments[String(index)];
  else state.assignments[String(index)] = id;
  return true;
}

export function roleOf(id: string): ProspectorRole {
  return prospectorDef(id).role;
}
